/**
 * AutoWizard - Adapter para @stepperize/react con API compatible con react-step-wizard
 *
 * Mantiene el patrón de children automático de react-step-wizard
 * pero usa @stepperize/react internamente para compatibilidad React 19
 */

import React, { useEffect, useMemo, useRef } from 'react';

import { defineStepper } from '@stepperize/react';

export interface AutoWizardProps {
  children?: React.ReactNode;
  nav?: React.ReactNode;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  onStepChange?: (stats: { activeStep: number; totalSteps: number }) => void;
  startIndex?: number;
  isLazyMount?: boolean;
  transitions?: object;
}

export interface InjectedStepProps {
  nextStep: () => void | Promise<void>;
  previousStep: () => void;
  goToStep: (stepIndex: number) => void;
  currentStep: number;
  totalSteps: number;
  firstStep: number;
  lastStep: number;
  isActive: boolean;
  stepper?: any;
}

const AutoWizard: React.FC<AutoWizardProps> = ({
  children,
  nav,
  footer,
  header,
  onStepChange,
  startIndex = 0,
  isLazyMount = false,
  transitions
}) => {
  void isLazyMount;
  void transitions;

  const childrenArray = React.Children.toArray(children).filter((child) =>
    React.isValidElement(child)
  );

  const stepperConfig = useMemo(() => {
    if (childrenArray.length === 0) {
      return null;
    }

    return defineStepper(
      childrenArray.map((_, index) => ({
        id: `step-${index}`,
        index
      }))
    );
  }, [childrenArray.length]);

  if (!stepperConfig) {
    return null;
  }

  const { Provider, useStepper } = stepperConfig;

  return (
    <Provider>
      <AutoWizardContent
        childrenArray={childrenArray}
        footer={footer}
        header={header}
        nav={nav}
        onStepChange={onStepChange}
        startIndex={startIndex}
        useStepper={useStepper}
      />
    </Provider>
  );
};

interface AutoWizardContentProps {
  childrenArray: Array<React.ReactNode>;
  nav?: React.ReactNode;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  useStepper: any;
  onStepChange?: (stats: { activeStep: number; totalSteps: number }) => void;
  startIndex: number;
}

const AutoWizardContent: React.FC<AutoWizardContentProps> = ({
  childrenArray,
  nav,
  footer,
  header,
  useStepper,
  onStepChange,
  startIndex
}) => {
  const stepper = useStepper({
    initialStep: `step-${startIndex}`
  });

  const stepHandlersRef = useRef<Map<number, () => Promise<boolean | void>>>(new Map());

  useEffect(() => {
    onStepChange?.({
      activeStep: stepper.index,
      totalSteps: stepper.count
    });
  }, [stepper.index, stepper.count, onStepChange]);

  const nextStep = async () => {
    const currentIndex = stepper.index;
    const handler = stepHandlersRef.current.get(currentIndex);

    if (handler) {
      try {
        const result = await handler();

        if (result === false) {
          return;
        }
      } catch (error) {
        console.error('Step validation error:', error);
        return;
      }
    }

    stepper.next();
  };

  const previousStep = () => {
    stepper.prev();
  };

  const goToStep = (stepIndex: number) => {
    const zeroIndexed = stepIndex - 1;
    if (zeroIndexed >= 0 && zeroIndexed < childrenArray.length) {
      stepper.goTo(`step-${zeroIndexed}`);
    }
  };

  const handleStep = (handler: () => Promise<boolean | void> | boolean | void) => {
    const currentIndex = stepper.state.current.index;
    const asyncHandler = async () => Promise.resolve(handler());
    stepHandlersRef.current.set(currentIndex, asyncHandler);
  };

  const injectedProps: InjectedStepProps = {
    nextStep,
    previousStep,
    goToStep,
    currentStep: stepper.index + 1,
    totalSteps: stepper.count,
    firstStep: 1,
    lastStep: stepper.count,
    isActive: true,
    stepper
  };

  const renderCurrentStep = () => {
    const currentChild = childrenArray[stepper.index];

    if (!React.isValidElement(currentChild)) {
      return currentChild;
    }

    return React.cloneElement(currentChild, {
      ...injectedProps,
      handleStep
    } as any);
  };

  const renderNav = () => {
    if (!nav || !React.isValidElement(nav)) {
      return nav;
    }

    return React.cloneElement(nav, {
      currentStep: stepper.index + 1,
      totalSteps: stepper.count,
      goToStep,
      ...stepper
    } as any);
  };

  return (
    <>
      {header}
      {renderNav()}
      {renderCurrentStep()}
      {footer}
    </>
  );
};

export { AutoWizard };
export default AutoWizard;
